import type { toLoggableEditorError } from '@/components/tiptap/utils/loggable-editor-error';
import { simpleMarkdownToHtml } from '@/helpers/markdownToHtml';
import { cn } from '@/lib/cn';
import { logger } from '@/utils/logger/logger';
import type {
  renderMarkdownToHtml,
  SafeEditorErrorSummary,
  toSafeEditorErrorSummary,
} from '@heidi/editor/core';
import { useIsMarkdownNativeEditorEnabled } from '@heidi/session/flags';
import { useIntersectionObserver } from '@heidi/utils';
import { useEffect, useMemo, useState } from 'react';
import sanitizeHtml from 'sanitize-html';

// The exact allowlist the legacy converter has always been sanitized with. The
// legacy path keeps it even for tags the converter can emit behind its own
// flags (fenced `<pre><code>` behind the block-editor codeblock flag), so a
// user without markdown-native sees the preview they saw before this component
// knew about the canonical renderer.
const LEGACY_ALLOWED_TAGS = [
  'p',
  'br',
  'hr',
  'strong',
  'em',
  'u',
  'ul',
  'ol',
  'li',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'table',
  'thead',
  'tbody',
  'tr',
  'th',
  'td',
];

// Emitted by the canonical renderer only: the section wrapper, the strike
// mark, and the block-level content the markdown-native kit can round-trip
// which the legacy regex converter never produced. Attributes stay stripped,
// so `<a>` renders as inert text and `data-section-title` never reaches the
// DOM — the section's `<h1>` already carries the title.
const CANONICAL_ALLOWED_TAGS = [
  ...LEGACY_ALLOWED_TAGS,
  'section',
  'blockquote',
  'pre',
  'code',
  'a',
  's',
];

const PROSE_CLASS = cn(
  'max-w-none break-words text-sm text-text-primary',
  '[&>h1:first-child]:mt-0 [&_h1]:mb-2 [&_h1]:mt-4 [&_h1]:text-base [&_h1]:font-semibold',
  '[&>h2:first-child]:mt-0 [&_h2]:mb-2 [&_h2]:mt-3 [&_h2]:text-sm [&_h2]:font-semibold',
  '[&_h3]:mb-1 [&_h3]:mt-2 [&_h3]:text-sm [&_h3]:font-medium',
  '[&_p]:my-2 [&_strong]:font-medium',
  '[&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5',
  '[&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5',
  '[&_li]:my-1',
  '[&_pre]:max-w-full [&_pre]:overflow-x-auto',
  // The canonical renderer nests every heading inside a `<section>`, so the
  // direct-child reset above reaches only the document's first heading —
  // matching the editor, where later sections keep their top margin.
  '[&>section:first-child>h1]:mt-0'
);

// Everything the canonical path needs at runtime, loaded together in one
// dynamic chunk: the renderer itself and the PHI-narrowing seam its failure
// log goes through. Both transitively link `@heidi/editor/core` (and with it
// the TipTap kernel), which must not ride the static import graph — this
// component sits on flag-off surfaces and the unauthenticated shared-template
// landing page.
interface CanonicalPreviewRenderer {
  renderMarkdownToHtml: typeof renderMarkdownToHtml;
  toSafeEditorErrorSummary: typeof toSafeEditorErrorSummary;
  toLoggableEditorError: typeof toLoggableEditorError;
}

// One shared load per page, not one per preview: a grid mounts an unbounded
// number of cards, and a single stale-chunk or offline failure must produce a
// single error record. The cache clears on rejection so a later mount (or a
// flag flip) retries instead of caching the failure forever.
let canonicalPreviewRendererPromise: Promise<CanonicalPreviewRenderer> | null = null;

const loadCanonicalPreviewRenderer = (): Promise<CanonicalPreviewRenderer> => {
  canonicalPreviewRendererPromise ??= Promise.all([
    import('@heidi/editor/core'),
    import('@heidi/editor/core'),
    import('@/components/tiptap/utils/loggable-editor-error'),
  ])
    .then(([renderer, safeError, loggableError]) => ({
      renderMarkdownToHtml: renderer.renderMarkdownToHtml,
      toSafeEditorErrorSummary: safeError.toSafeEditorErrorSummary,
      toLoggableEditorError: loggableError.toLoggableEditorError,
    }))
    .catch((error: unknown) => {
      // A load failure (offline, stale chunk) leaves the legacy preview in
      // place — the same degradation as a render throw. The error comes from
      // the module loader before any template content is in play, so its name
      // carries no content.
      // Clear the cache before logging: if the logger itself throws, the
      // retry invariant must still hold.
      canonicalPreviewRendererPromise = null;
      logger.error('Template preview canonical renderer failed to load', {
        error: error instanceof Error ? error.name : 'unknown',
      });
      throw error;
    });
  return canonicalPreviewRendererPromise;
};

interface TemplateMarkdownPreviewProps {
  markdown: string;
  className?: string;
  /** Keep legacy HTML until this preview first enters the viewport. */
  deferCanonicalUntilVisible?: boolean;
}

interface PreviewRender {
  html: string;
  /** Set when the canonical renderer threw and the legacy fallback rendered. */
  failure: {
    error: Readonly<SafeEditorErrorSummary>;
    contentLength: number;
  } | null;
}

const renderLegacy = (markdown: string): string =>
  sanitizeHtml(simpleMarkdownToHtml({ markdown }) ?? '', {
    allowedTags: LEGACY_ALLOWED_TAGS,
    allowedAttributes: {},
  });

export function TemplateMarkdownPreview({
  markdown,
  className,
  deferCanonicalUntilVisible = false,
}: TemplateMarkdownPreviewProps) {
  const isMarkdownNative = useIsMarkdownNativeEditorEnabled();
  const shouldDeferCanonical = deferCanonicalUntilVisible && isMarkdownNative;
  const [containerRef, entry] = useIntersectionObserver<HTMLDivElement>({
    threshold: 0,
    // Load slightly before the card is on screen so the legacy→canonical
    // swap (and the <section> reflow) happens offscreen.
    rootMargin: '200px',
  });
  const [hasBeenVisible, setHasBeenVisible] = useState(false);
  useEffect(() => {
    if (entry?.isIntersecting === true) setHasBeenVisible(true);
  }, [entry?.isIntersecting]);

  const observerUnavailable =
    shouldDeferCanonical && typeof IntersectionObserver === 'undefined';
  const canonicalEligible =
    !shouldDeferCanonical || observerUnavailable || hasBeenVisible;

  // The canonical renderer builds a headless editor, which needs a DOM.
  // Loading it from an effect keeps the server paint and the first client
  // render identical (legacy), so a pre-rendered page never sees a hydration
  // mismatch — and flag-off users never download the editor kernel at all.
  const [canonical, setCanonical] = useState<CanonicalPreviewRenderer | null>(null);
  useEffect(() => {
    if (!isMarkdownNative || !canonicalEligible) return undefined;
    let cancelled = false;
    loadCanonicalPreviewRenderer()
      .then((module) => {
        if (!cancelled) setCanonical(module);
      })
      // Logged once inside the shared loader; each subscriber only swallows.
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isMarkdownNative, canonicalEligible]);

  const canonicalRenderer = isMarkdownNative && canonicalEligible ? canonical : null;

  // Memoized because the canonical renderer constructs and destroys a headless
  // editor per call, and the template grid renders one preview per card. The
  // memo stays pure — a render failure is captured here and logged in the
  // effect below, so a discarded or StrictMode-doubled render never logs.
  const { html, failure } = useMemo<PreviewRender>(() => {
    if (!canonicalRenderer) {
      return { html: renderLegacy(markdown), failure: null };
    }
    try {
      return {
        html: sanitizeHtml(canonicalRenderer.renderMarkdownToHtml(markdown), {
          allowedTags: CANONICAL_ALLOWED_TAGS,
          allowedAttributes: {},
        }),
        failure: null,
      };
    } catch (error) {
      // A preview is read-only decoration; a throw here would take out a whole
      // grid of template cards. Fall back to the legacy converter, but log it
      // — the fallback silently rendering a lesser preview forever is exactly
      // the failure this component already had. Narrowed name and length only:
      // `Error#name` is writable, and template markdown must not reach app
      // logs.
      return {
        html: renderLegacy(markdown),
        failure: {
          error: canonicalRenderer.toLoggableEditorError(
            canonicalRenderer.toSafeEditorErrorSummary(error),
            'canonical_render_failed'
          ),
          contentLength: markdown.length,
        },
      };
    }
  }, [markdown, canonicalRenderer]);

  useEffect(() => {
    if (failure) {
      logger.error('Template preview canonical render failed', failure);
    }
  }, [failure]);

  return (
    <div
      ref={shouldDeferCanonical && !hasBeenVisible ? containerRef : undefined}
      className={cn(PROSE_CLASS, className)}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
